import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, Eye, EyeOff, Loader2, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSiteContent } from '../hooks/useSiteContent';

import { BACKEND_URL } from '../config/api';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { get } = useSiteContent();
  const token = searchParams.get('token');
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  // Password strength checks
  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
  });

  useEffect(() => {
    // Validate password strength as user types
    setPasswordChecks({
      length: newPassword.length >= 8,
      uppercase: /[A-Z]/.test(newPassword),
      lowercase: /[a-z]/.test(newPassword),
      number: /[0-9]/.test(newPassword),
    });
  }, [newPassword]);

  const isPasswordValid = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isPasswordValid) {
      toast.error('Please meet all password requirements');
      return;
    }

    if (!passwordsMatch) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to reset password';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // No token provided
  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Invalid Reset Link</h2>
          <p className="text-slate-600 mb-6">
            This password reset link is invalid or has been used already.
          </p>
          <button 
            onClick={() => navigate('/forgot-password')} 
            className="btn-primary w-full mb-3"
          >
            Request New Reset Link
          </button>
          <button 
            onClick={() => navigate('/login')} 
            className="btn-secondary w-full"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">{get('auth.reset.success_title', 'Password Reset Complete')}</h2>
          <p className="text-slate-600 mb-6">
            {get('auth.reset.success_desc', 'Your password has been reset. You can now sign in.')}
          </p>
          <button 
            onClick={() => navigate('/login')} 
            className="btn-primary w-full"
            data-testid="goto-login-btn"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Error state (expired/invalid token)
  if (error && (error.includes('expired') || error.includes('Invalid'))) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="h-8 w-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">{get('auth.reset.expired_link', 'Link Expired')}</h2>
          <p className="text-slate-600 mb-6">
            {error}
          </p>
          <button 
            onClick={() => navigate('/forgot-password')} 
            className="btn-primary w-full mb-3"
          >
            Request New Reset Link
          </button>
          <button 
            onClick={() => navigate('/login')} 
            className="btn-secondary w-full"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // Main reset form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-xl shadow-lg p-8 border border-slate-200">
          <div className="flex items-center justify-center gap-2 mb-8">
            <Building2 className="h-8 w-8 text-orange-500" />
            <h1 className="text-2xl font-bold text-slate-900">OnsiteHand</h1>
          </div>

          <h2 className="text-2xl font-bold text-slate-900 mb-2 text-center">{get('auth.reset.title', 'Reset Password')}</h2>
          <p className="text-slate-600 text-center mb-6">{get('auth.reset.subtitle', 'Choose a new secure password')}</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-slate-700 mb-1">
                {get('auth.reset.new_password_label', 'New Password')}
              </label>
              <div className="relative">
                <input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  placeholder="Enter new password"
                  className="w-full px-4 py-2 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              
              {/* Password strength indicators */}
              <div className="mt-3 space-y-1">
                <p className="text-xs text-slate-500 font-medium mb-2">{get('auth.reset.password_requirements', 'Password must have:')}</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className={`flex items-center gap-1.5 text-xs ${passwordChecks.length ? 'text-green-600' : 'text-slate-400'}`}>
                    {passwordChecks.length ? <CheckCircle className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                    8+ characters
                  </div>
                  <div className={`flex items-center gap-1.5 text-xs ${passwordChecks.uppercase ? 'text-green-600' : 'text-slate-400'}`}>
                    {passwordChecks.uppercase ? <CheckCircle className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                    Uppercase letter
                  </div>
                  <div className={`flex items-center gap-1.5 text-xs ${passwordChecks.lowercase ? 'text-green-600' : 'text-slate-400'}`}>
                    {passwordChecks.lowercase ? <CheckCircle className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                    Lowercase letter
                  </div>
                  <div className={`flex items-center gap-1.5 text-xs ${passwordChecks.number ? 'text-green-600' : 'text-slate-400'}`}>
                    {passwordChecks.number ? <CheckCircle className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                    Number
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 mb-1">
                {get('auth.reset.confirm_password_label', 'Confirm Password')}
              </label>
              <input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm new password"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  confirmPassword && !passwordsMatch ? 'border-red-300' : 'border-slate-300'
                }`}
                data-testid="confirm-password-input"
              />
              {confirmPassword && !passwordsMatch && (
                <p className="text-xs text-red-600 mt-1">{get('auth.reset.passwords_dont_match', 'Passwords do not match')}</p>
              )}
            </div>

            {error && !error.includes('expired') && !error.includes('Invalid') && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !isPasswordValid || !passwordsMatch}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
